<template>
    <RouterLink
        v-if="to"
        :to="to"
        class="inline-block transition-all"
        :class="[defaultClass, iconRelatedClasses, classes, sizeClasses, loading && loadingClass, disabled && disabledClass]"
        v-bind="$attrs"
        :disabled="disabled"
    >
        <span v-if="icon" :class="[iconClasses, 'left-4']">
            <i class="fa-sharp" :class="`fa-${icon} fa-${iconType}`"/>
        </span>

        <slot>{{ text }}</slot>

        <span v-if="iconRight" :class="[iconClasses, 'right-3']">
            <i class="fa-sharp" :class="`fa-${iconRight} fa-${iconType}`"/>
        </span>
    </RouterLink>
    <a
        v-else-if="href"
        :href="href"
        class="inline-block transition-all"
        :class="[defaultClass, iconRelatedClasses, classes, sizeClasses, loading && loadingClass, disabled && disabledClass]"
        v-bind="$attrs"
    >
        <span v-if="icon" :class="[iconClasses, 'left-4']">
            <i class="fa-sharp" :class="`fa-${icon} fa-${iconType}`"/>
        </span>

        <slot>{{ text }}</slot>

        <span v-if="iconRight" :class="[iconClasses, 'right-3']">
            <i class="fa-sharp" :class="`fa-${iconRight} fa-${iconType}`"/>
        </span>
    </a>
    <button
        v-else
        :type="type"
        :class="[defaultClass, iconRelatedClasses, classes, sizeClasses, loading && loadingClass, disabled && disabledClass]"
        v-bind="$attrs"
        :disabled="loading || disabled"
        class="transition-all"
    >
        <span v-if="icon" :class="[iconClasses, 'left-3']">
            <i class="fa-sharp fa-light" :class="`fa-${icon}`"/>
        </span>

        <slot>{{ text }}</slot>

        <Transition name="fade">
            <span v-if="recentlySucceeded" :class="[iconClasses, 'right-3']">
                <i class="fa-sharp fa-light fa-check"/>
            </span>
            <span v-else-if="recentlyFailed" :class="[iconClasses, 'right-3']">
                <i class="fa-sharp fa-light fa-triangle-exclamation"/>
            </span>
            <span v-else-if="loading" :class="[iconClasses, 'right-3']">
                <i class="fa-sharp fa-light fa-spinner-third fa-spin"/>
            </span>
            <span v-else-if="iconRight" :class="[iconClasses, 'right-3']">
                <i class="fa-sharp fa-light" :class="`fa-${iconRight}`"/>
            </span>
        </Transition>
    </button>

    <TranslateText :text="trans" class="relative right-1 -top-3"/>
</template>

<script setup>
import { computed, useSlots } from 'vue'
import TranslateText from "@/components/TranslateText.vue";

const slots = useSlots();
const defaultClass = 'my-auto font-medium items-center border shadow-sm rounded-xl focus:ring-offset-2'
const brandClass = 'bg-brand hover:bg-brand-dark text-white border-transparent'
const primaryClass = 'bg-slate-600 hover:bg-black text-white border-transparent'
const secondaryClass = 'border-gray-800 text-gray-700 bg-white hover:bg-gray-800 hover:text-white dark:hover:bg-slate-800'
const dangerClass = 'bg-red-600 hover:bg-red-700 text-white border-transparent'
const warningClass = 'bg-yellow-400 hover:bg-yellow-500 -300 text-white border-transparent'
const positiveClass = 'bg-green-600 hover:bg-green-700 text-white border-transparent'
const loadingClass = 'animate-pulse cursor-not-allowed'
const disabledClass = 'cursor-not-allowed opacity-50'

const props = defineProps({
    type: {
        required: false,
        default: 'button'
    },

    text: String,
    trans: String,
    loading: Boolean,
    recentlySucceeded: Boolean,
    recentlyFailed: Boolean,
    disabled: Boolean,
    to: String,
    href: String,

    brand: Boolean,
    primary: Boolean,
    secondary: Boolean,

    small: Boolean,
    mini: Boolean,

    icon: String,
    iconRight: String,
    iconType: {
        required: false,
        type: String,
        default: 'light'
    },

    positive: Boolean,
    danger: Boolean,
    warning: Boolean,
});

const iconRelatedClasses = computed(() => {
    if (onlyIcon.value) {
        return 'relative';
    }

    if (props.icon) {
        return 'relative pl-10';
    }

    if (props.iconRight || props.loading || props.recentlySucceeded || props.recentlyFailed) {
        return 'relative pr-10';
    }

    return '';
});

const classes = computed(() => {
    if (props.recentlySucceeded) {
        return positiveClass
    }

    if (props.recentlyFailed) {
        return dangerClass
    }

    if (props.brand) {
        return brandClass
    }

    if (props.secondary) {
        return secondaryClass
    }

    if (props.positive) {
        return positiveClass
    }

    if (props.danger) {
        return dangerClass
    }

    if (props.warning) {
        return warningClass
    }

    return primaryClass
});

const sizeClasses = computed(() => {
    if (props.mini) {
        return 'px-3 py-1.5 text-xs leading-4'
    }

    if (props.small) {
        return 'px-3 py-2 text-xs leading-4'
    }

    return 'px-4 py-2 text-sm'
});

const iconClasses = computed(() => {
    let classes = '';

    if (props.small) {
        if (! onlyIcon.value) {
            classes += '-ml-0.5';
        }

        classes += ' h-4 w-4'
    } else {
        if (! onlyIcon.value) {
            classes += '-ml-1';
        }

        classes += ' h-5 w-5'
    }

    if (! onlyIcon.value) {
        classes += ' absolute top-1/2 -translate-y-1/2 flex items-center justify-center';
    }

    return classes
});

const onlyIcon = computed(() => {
    if (! props.icon) {
        return false;
    }

    return ! props.text && ! slots.default;
});

</script>

<style>
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
